import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import PageSeo, { BASE } from "@/components/feature/PageSeo";
import { events } from "@/mocks/events";

const catOptions = ["全部", ...Array.from(new Set(events.map((e) => e.categoryLabel)))];

export default function Shijian() {
  const [cat, setCat] = useState("全部");

  const list = useMemo(() => (cat === "全部" ? events : events.filter((e) => e.categoryLabel === cat)), [cat]);

  return (
    <>
      <PageSeo
        title="走红大事记｜吃瓜爆网走红事件追踪与爆红人物盘点"
        description="吃瓜爆网走红大事记按时间线记录最新出圈事件，从素人一夜爆红到爆红人物走红全过程，逐条复盘每一次走红背后的真实路径。"
        keywords="吃瓜爆网走红事件,走红大事记,素人出圈,网红走红,爆红人物事件"
        path="/shijian"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "走红大事记",
            url: BASE + "/shijian",
            about: "新晋网红走红事件追踪",
          },
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "走红大事记",
            itemListElement: events.map((e, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: e.title,
              url: BASE + `/shijian/${e.id}`,
            })),
          },
        ]}
      />
      <div className="min-h-screen bg-background-50">
        <Header />
        <main>
          <section className="border-b border-background-200 bg-background-100">
            <div className="container-x py-10 md:py-14">
              <span className="eyebrow">
                <i className="ri-article-line" aria-hidden="true" />
                走红大事记
              </span>
              <h1 className="mt-4 max-w-3xl font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-5xl">
                从素人到顶流的出圈之路全记录
              </h1>
              <p className="mt-5 max-w-2xl text-[15px] leading-7 text-foreground-700 md:text-base">
                我们把每一次真实的走红事件按时间整理成档案，还原现场、复盘路径、讲清规律。追更不停，精彩主角持续更新。
              </p>
            </div>
            <Breadcrumb items={[{ label: "走红大事记" }]} />
          </section>

          <section className="container-x py-10 md:py-14">
            <div className="flex flex-wrap items-center gap-2" aria-label="事件栏目筛选">
              {catOptions.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCat(c)}
                  className={`rounded-full px-4 py-1.5 font-label text-sm transition-colors cursor-pointer ${
                    cat === c
                      ? "bg-background-950 text-background-50"
                      : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                  }`}
                >
                  {c}
                </button>
              ))}
              <span className="ml-auto font-label text-sm text-foreground-500">共 {list.length} 条记录</span>
            </div>

            <div className="mt-8 space-y-5">
              {list.map((e, idx) => (
                <article key={e.id} className="card group p-5 transition-colors hover:border-primary-300 md:p-6">
                  <div className="flex flex-col gap-5 md:flex-row md:items-center">
                    <div className="flex items-center gap-4 md:w-40 md:shrink-0">
                      <span className="font-heading text-2xl font-black text-primary-500">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <div className="text-xs leading-5 text-foreground-600">
                        <p className="flex items-center gap-1">
                          <i className="ri-calendar-line" aria-hidden="true" />
                          {e.date}
                        </p>
                        <p className="mt-1 inline-flex items-center gap-1">
                          <i className="ri-fire-line text-primary-500" aria-hidden="true" />
                          热度 {e.heat}
                        </p>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-sm bg-accent-100 px-2 py-0.5 font-label text-xs text-accent-900">
                          {e.categoryLabel}
                        </span>
                        <span className="text-xs text-foreground-600">主角 · {e.person}</span>
                        <span className="ml-auto inline-flex items-center gap-1 text-xs text-foreground-600">
                          <i className="ri-time-line" aria-hidden="true" />
                          阅读约 {e.read}
                        </span>
                      </div>
                      <Link to={`/shijian/${e.id}`} className="group/link cursor-pointer">
                        <h2 className="mt-2.5 font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-xl">
                          {e.title}
                        </h2>
                      </Link>
                      <p className="mt-2 text-sm leading-7 text-foreground-700 md:line-clamp-2">{e.excerpt}</p>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {e.tags.map((t) => (
                          <span key={t} className="rounded-sm bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                    <Link
                      to={`/shijian/${e.id}`}
                      className="hidden shrink-0 items-center justify-center gap-1.5 rounded-md border border-background-300 px-4 py-2.5 font-label text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 lg:inline-flex cursor-pointer"
                    >
                      阅读全文
                      <i className="ri-arrow-right-line" aria-hidden="true" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}